import React, { useState } from "react";
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import mixpanel from 'mixpanel-browser';
import { post } from '../../services/BackendService';
import { trackFormSubmit } from '../../helpers/analytics';
import "./InquiryFormComponent.scss";

const InquiryFormComponent = ({ 
  title = "Ready to Book?",
  subtitle = "Contact us for more information",
  features = [
    "Expert local guides",
    "Customizable experiences",
    "Comfortable transportation",
    "Flexible scheduling"
  ],
  defaultMessage = "Hello, I am interested in this service.",
  bookingReference = "Service Inquiry",
  includeWhatsApp = true,
  phoneNumber = "36707177656",
  section = "general"
}) => {
  const [contactForm, setContactForm] = useState({
    full_name: '',
    email_address: '',
    phone_number: '',
    message: defaultMessage,
  });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleContactFormChange = ({target}) => {
    setContactForm(prevForm => ({
      ...prevForm,
      [target.id]: target.value
    }));
  }

  const submitContactForm = (event) => {
    event.preventDefault();
    
    // Prevent multiple submissions
    if (isLoading) return;
    
    if (event.target.checkValidity()) {
      setIsLoading(true);
      event.target.classList.remove('was-validated');

      mixpanel.identify(contactForm.email_address);
      mixpanel.people.set({
        $name: contactForm.full_name,
        $email: contactForm.email_address,
      });
      mixpanel.track('Inquiry Form Submitted', {
        booking_reference: bookingReference
      });
      
      // Track event with Google Analytics
      trackFormSubmit('service_inquiry', section, {
        booking_reference: bookingReference,
        full_name: contactForm.full_name,
        email_address: contactForm.email_address
      });

      post('/sendEmail', {
        ...contactForm,
        booking_reference: bookingReference
      })
        .then(response => {
          setSent(true);
          setContactForm({
            full_name: '',
            email_address: '',
            phone_number: '',
            message: '',
          });
        })
        .catch((error) => {
          setError(error.response?.data?.error?.message || 'An error occurred');
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      event.target.classList.add('was-validated');
    }
  }

  return (
    <section className="inquiry-form-section">
      <div className="container">
        <div className="inquiry-card">
          <div className="row">
            <div className="col-lg-6">
              <div className="inquiry-info">
                <h2>{title}</h2>
                <p className="lead">{subtitle}</p>
                <ul className="inquiry-features">
                  {features.map((feature, index) => (
                    <li key={index}><i className="fa fa-check-circle"></i> {feature}</li>
                  ))}
                </ul>
                
                {includeWhatsApp && (
                  <div className="inquiry-whatsapp">
                    <p>Prefer to discuss your needs directly?</p>
                    <a href={`https://wa.me/${phoneNumber}`} target="_blank" rel="noopener noreferrer" className="btn btn-whatsapp">
                      <i className="fa-brands fa-whatsapp"></i> Contact via WhatsApp
                    </a>
                  </div>
                )}
              </div>
            </div>
            <div className="col-lg-6">
              {!sent ? (
                <form className="inquiry-form needs-validation" noValidate onSubmit={submitContactForm}>
                  <div className="row gy-3">
                    <div className="col-md-6 col-12">
                      <div className="form-group">
                        <label htmlFor="full_name">Full Name</label>
                        <Form.Control 
                          type="text" 
                          className="form-control" 
                          id="full_name" 
                          required
                          onChange={handleContactFormChange}
                          value={contactForm.full_name}
                        />
                      </div>
                    </div>

                    <div className="col-md-6 col-12">
                      <div className="form-group">
                        <label htmlFor="phone_number">Phone Number</label>
                        <Form.Control 
                          type="text" 
                          className="form-control" 
                          id="phone_number" 
                          required
                          onChange={handleContactFormChange}
                          value={contactForm.phone_number}
                        />
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <label htmlFor="email_address">E-mail Address</label>
                        <Form.Control 
                          type="email" 
                          className="form-control" 
                          id="email_address" 
                          required
                          onChange={handleContactFormChange}
                          value={contactForm.email_address}
                        />
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <label htmlFor="message">Message</label>
                        <textarea 
                          className="form-control" 
                          id="message" 
                          rows="4"
                          required
                          onChange={handleContactFormChange}
                          value={contactForm.message}
                        />
                      </div>
                    </div>

                    <div className="col-12">
                      <button 
                        type="submit" 
                        className="btn btn-primary btn-lg w-100"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Submitting...
                          </>
                        ) : (
                          'Submit Inquiry'
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              ) : (
                <Alert variant="success" className="text-center success-message">
                  <Alert.Heading>Thank You!</Alert.Heading>
                  <p>
                    We've received your inquiry and will contact you shortly.<br />
                    For urgent matters, please contact us via WhatsApp.
                  </p>
                </Alert>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InquiryFormComponent;
